import {
  useEffect,
  useRef,
  useState,
  createRef,
  RefObject,
  useCallback
} from "react";
import { useKelasLiveContext } from "../../../stores/kelas-live/kelas-live-context";
import {
  getJoinClassLink,
  getSessionsForUser,
  getStudentSessionsBySlot
} from "../../../services/api/sessions/sessionsService";
import {
  ClassSession,
  SubscribedCourse,
  SubscriptionType
} from "../../../services/api/sessions/sessionService.types";
import { differenceInCalendarWeeks, endOfWeek, startOfWeek } from "date-fns";
import { ClassSessionType } from "../hooks/use-user-landing/use-user-landing.types";
import { useBoolean } from "../../../hooks/use-boolean/use-boolean";
import { getPageName } from "../../../utils/utils";
import trackEvent from "../../../services/mixpanel/mixpanel";
import { kelasLiveEvents } from "../../../services/mixpanel/kelas-live-events";
import { useToast } from "../../atoms/toast/toast-context";
import * as Sentry from "@sentry/nextjs";
import { weeklyScheduleEvents } from "../../../services/mixpanel/weekly-schedule-events";
import { getSourceInfoEvent } from "../../../utils/mixpanel/mixpanel-utils";
import { useGetCurrentPlatform } from "../../../hooks/use-media-query/use-get-currrent-platform";
import { trackClassmonEvent } from "services/api/classmon/classmonService";

export const useWeeklySchedule = () => {
  const { userSubscription, user } = useKelasLiveContext();
  const primaryUser = user?.userData.primary_user || null;
  const { subscriptionStatus, fetchSubscriptionStatus } =
    userSubscription || {};
  const { addToast } = useToast();
  const [selectedCourse, setSelectedCourse] = useState<
    undefined | SubscribedCourse
  >(subscriptionStatus?.subscribedCourses?.[0]);
  const [sessions, setSessions] = useState<ClassSession[] | []>([]);
  const [slotStartDate, setSlotStartDate] = useState(new Date().toISOString());
  const [slotEndDate, setSlotEndDate] = useState(new Date().toISOString());
  const [noOfWeeks, setNoOfWeeks] = useState<null | number>(
    differenceInCalendarWeeks(new Date(slotEndDate), new Date(slotStartDate))
  );
  const [selectedWeekStartDate, setSelectedWeekStartDate] = useState(
    startOfWeek(new Date(), { weekStartsOn: 1 }).toISOString()
  );
  const [selectedWeekEndDate, setSelectedWeekEndDate] = useState(
    endOfWeek(new Date(), { weekStartsOn: 1 }).toISOString()
  );

  type Course = {
    course: {
      name: string;
    };
    courseId: string;
    slotId: string;
  };

  const [subscribedCourses, setSubscribedCourses] = useState<Course[] | []>([]);

  const [baseMediaUrl, setBaseMediaUrl] = useState("");
  const currentTimeStamp = new Date().toISOString();
  const [selectedPastSession, setSelectedPastSession] =
    useState<ClassSession | null>(null);
  const [modalClassSession, setModalClassSession] =
    useState<ClassSession | null>(null);
  const [modalType, setModalType] = useState<ClassSessionType | null>(null);
  const [showJoiningClassPopUp, setShowJoiningClassPopUp] = useBoolean();
  const scrollRefs = useRef<RefObject<HTMLDivElement>[]>([]);
  const currentPlatform = useGetCurrentPlatform();

  scrollRefs.current = [...Array(noOfWeeks)].map(
    (_, i) => scrollRefs.current[i] ?? createRef()
  );

  useEffect(() => {
    trackEvent({
      eventName: weeklyScheduleEvents.weekDetailViewed,
      payload: {
        sourceInfo: getSourceInfoEvent(currentPlatform)
      }
    });

    return () => {
      trackEvent({
        eventName: weeklyScheduleEvents.weekDetailClosed,
        payload: {
          sourceInfo: getSourceInfoEvent(currentPlatform)
        }
      });
    };
  }, []);

  useEffect(() => {
    if (scrollRefs.current.length === noOfWeeks) {
      const classNameArr: string[] = [];
      scrollRefs.current.map((ref, i) => {
        if (
          i > 0 &&
          ref.current?.className &&
          !classNameArr.includes(ref.current?.className)
        ) {
          scrollIntoView(i);
        }
        if (ref.current?.className) {
          classNameArr.push(ref.current?.className);
        }
      });
    }
  }, [noOfWeeks]);

  useEffect(() => {
    if (primaryUser?.access_token)
      fetchSubscriptionStatus(primaryUser?.access_token);
  }, [primaryUser]);

  useEffect(() => {
    if (subscriptionStatus?.subscriptionType === SubscriptionType.PAID) {
      setSelectedCourse(subscriptionStatus?.subscribedCourses?.[0]);
    }
  }, [subscriptionStatus]);

  useEffect(() => {
    if (subscriptionStatus?.subscriptionType !== SubscriptionType.PAID) {
      fetchSessionsForUser();
    }
  }, [subscriptionStatus]);

  useEffect(() => {
    if (selectedCourse) {
      getStudentSessionsForSlot(selectedWeekStartDate, selectedWeekEndDate);
    }
  }, [selectedCourse]);

  const fetchSessionsForUser = useCallback(
    async (focusExamIds?: Array<string>) => {
      try {
        const response = await getSessionsForUser(
          primaryUser.access_token,
          focusExamIds
        );

        const constructedResponse = response.data.courses.map((course) => {
          return {
            course: {
              name: course.name
            },
            courseId: course.id,
            slotId: course.slot[0].id
          };
        });

        setSubscribedCourses(constructedResponse);
        setSelectedCourse(constructedResponse[0]);
      } catch (err) {
        Sentry.captureException(err);
      }
    },
    [primaryUser?.access_token]
  );

  const handleChangeCourse = (course: SubscribedCourse) => {
    setSessions([]);
    setSelectedWeekStartDate(
      startOfWeek(new Date(), { weekStartsOn: 1 }).toISOString()
    );
    setSelectedWeekEndDate(
      endOfWeek(new Date(), { weekStartsOn: 1 }).toISOString()
    );
    setSelectedCourse(course);

    trackEvent({
      eventName: weeklyScheduleEvents.weekDetailPageCourseFiltered,
      payload: {
        CourseId: selectedCourse?.courseId,
        name: primaryUser?.full_name,
        sourceInfo: getSourceInfoEvent(currentPlatform),
        Curriculum: primaryUser.curriculum,
        course: course?.course.name,
        slot: course?.slotId,
        slotTimeEnd: slotEndDate,
        userType: subscriptionStatus?.subscriptionType,
        weekStartDate: startOfWeek(new Date(), {
          weekStartsOn: 1
        }).toISOString(),
        weekEndDate: endOfWeek(new Date(), { weekStartsOn: 1 }).toISOString(),
        week: null
      }
    });
  };

  const handleWeeklySessionClick = (
    startDate: string,
    endDate: string,
    index: number
  ) => {
    scrollIntoView(index);
    setSelectedWeekStartDate(startDate);
    setSelectedWeekEndDate(endDate);
    getStudentSessionsForSlot(startDate, endDate);
  };

  const getStudentSessionsForSlot = async (
    slotStartDate: string,
    slotEndDate: string
  ) => {
    if (selectedCourse) {
      const res = await getStudentSessionsBySlot(
        primaryUser?.access_token,
        selectedCourse.slotId,
        slotStartDate,
        slotEndDate
      );

      setBaseMediaUrl(res.data.mediaBaseUrl);
      setSlotStartDate(res.data.courses[0].slot[0].startDate);
      setSlotEndDate(res.data.courses[0].slot[0].endDate);

      setNoOfWeeks(
        differenceInCalendarWeeks(
          endOfWeek(new Date(res.data.courses[0].slot[0].endDate), {
            weekStartsOn: 1
          }),
          startOfWeek(new Date(res.data.courses[0].slot[0].startDate), {
            weekStartsOn: 1
          })
        )
      );

      const classSessions: ClassSession[] | [] = [];
      res.data.courses.map((course) => {
        return course.slot.map((courseSlot) => {
          const sortedClassSession: ClassSession[] = courseSlot.session.sort(
            (a, b) => {
              // @ts-ignore
              return new Date(a.endDate) - new Date(b.endDate);
            }
          );
          // @ts-ignore
          classSessions.push(...sortedClassSession);
        });
      });

      if (!classSessions.length) {
        trackEvent({
          eventName: weeklyScheduleEvents.noSchedulePageViewed,
          payload: {
            CourseId: selectedCourse?.courseId,
            name: primaryUser?.full_name,
            weekStartDate: selectedWeekStartDate,
            weekEndDate: selectedWeekEndDate,
            sourceInfo: getSourceInfoEvent(currentPlatform),
            Curriculum: primaryUser.curriculum,
            course: selectedCourse.course.name,
            slot: selectedCourse.slotId,
            slotTimeEnd: slotEndDate,
            userType: subscriptionStatus?.subscriptionType,
            week: null
          }
        });
      }

      setSessions(classSessions);
    }
  };

  const scrollIntoView = (scrollIndex: number) => {
    scrollRefs.current[scrollIndex]?.current?.scrollIntoView({
      inline: "center",
      block: "end",
      behavior: "smooth"
    });
  };

  const getJoinLinkURL = async (classID: string) => {
    trackClassmonEvent("joining", primaryUser.id, classID);

    const pageName = getPageName();

    trackEvent({
      eventName: kelasLiveEvents.liveClassJoinButtonPressed,
      payload: {
        userType: subscriptionStatus?.subscriptionType,
        incomingSource: pageName,
        liveHomePageDesignVersion: "Version 2 - Blue Bulletin Board"
      }
    });
    setShowJoiningClassPopUp.on();
    setModalClassSession(null);
    setModalType(null);
    try {
      const joinClassResponse = await getJoinClassLink(
        primaryUser.access_token,
        classID,
        primaryUser.id
      );
      const zoomMeetingLink = joinClassResponse.data.join_url;
      trackEvent({
        eventName: kelasLiveEvents.liveClassJoined,
        payload: {
          userType: subscriptionStatus?.subscriptionType,
          platform: "website"
        }
      });
      setShowJoiningClassPopUp.off();
      setTimeout(() => {
        window.open(zoomMeetingLink, "_blank");
      });
    } catch (err) {
      const error = err as { json?: { errors?: string[] } };
      if (
        error.json?.errors?.includes(
          "LiveClassErrors::StudentNotEligibleForClass"
        ) &&
        primaryUser?.access_token
      ) {
        userSubscription.fetchSubscriptionStatus(primaryUser?.access_token);
      } else {
        addToast({
          heading: "Something went wrong, Try again",
          variant: "error"
        });
      }
      Sentry.captureException(err);
      trackEvent({
        eventName: kelasLiveEvents.liveClassJoinError,
        payload: {
          userType: subscriptionStatus?.subscriptionType,
          error: JSON.stringify(err)
        }
      });
      setShowJoiningClassPopUp.off();
      addToast({
        heading: "Something went wrong, Try again",
        variant: "error"
      });
    }
  };

  return {
    subscribedCourses:
      subscriptionStatus?.subscriptionType === SubscriptionType.PAID
        ? subscriptionStatus?.subscribedCourses
        : subscribedCourses,
    selectedCourse,
    setSelectedCourse,
    handleChangeCourse,
    noOfWeeks,
    slotStartDate,
    handleWeeklySessionClick,
    slotEndDate,
    selectedWeekStartDate,
    selectedWeekEndDate,
    sessions,
    baseMediaUrl,
    primaryUser,
    subscriptionStatus,
    currentTimeStamp,
    selectedPastSession,
    setSelectedPastSession,
    modalClassSession,
    setModalClassSession,
    modalType,
    setModalType,
    showJoiningClassPopUp,
    setShowJoiningClassPopUp,
    getJoinLinkURL,
    scrollRefs
  };
};
